import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCloudflareAuth } from '../../contexts/CloudflareAuthContext';
import { motion } from 'framer-motion';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../services/api';
import GoogleLoginButton from '../../components/GoogleLoginButton';

const Login = () => {
  const navigate = useNavigate();
  const { beginLogin, verifyLoginOtp } = useCloudflareAuth();
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const normalizedEmail = formData.email.trim().toLowerCase();
      const res = await beginLogin(normalizedEmail, formData.password);
      if (res.otpRequired) {
        toast.success(res.message || 'OTP sent to your email');
        setOtpMode(true);
        return;
      }

      toast.success('Login successful');
      sessionStorage.setItem('showLoginSuccess', 'true');
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Invalid login credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpLoading(true);
    try {
      const normalizedEmail = formData.email.trim().toLowerCase();
      const success = await verifyLoginOtp(normalizedEmail, otp.trim());
      if (success) {
        toast.success('Login successful');
        sessionStorage.setItem('showLoginSuccess', 'true');
        navigate('/dashboard', { replace: true });
      }
    } catch (error: any) {
      console.error('OTP verify error:', error);
      toast.error(error.message || 'Invalid or expired OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const normalizedEmail = formData.email.trim().toLowerCase();
      const res = await api.resendOtp(normalizedEmail, 'login');
      if (res.success) toast.success(res.message || 'OTP resent');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend OTP');
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 -mt-24 p-4">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gray-900/50 via-gray-900/50 to-gray-900/90" />
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute w-screen h-screen">
          <div className="absolute top-0 left-0 w-[200%] h-[200%] origin-top-left bg-gradient-to-br from-blue-500/10 to-purple-500/10 animate-rotate-slow" />
          <div className="absolute bottom-0 right-0 w-[200%] h-[200%] origin-bottom-right bg-gradient-to-tl from-indigo-500/10 to-pink-500/10 animate-rotate-slow-reverse" />
        </div>
      </div>

      {/* Login Form */}
      <div className="max-w-md w-full relative z-10 mx-auto px-4">
        <div className="floating-card glass-effect rounded-2xl p-8 shadow-2xl backdrop-blur-xl">
          {/* Logo Section */}
          <div className="text-center space-y-4 mb-6">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-full blur-2xl opacity-75 animate-pulse" />
              <Link to="/">
                <img
                  src="/images/logo.png"
                  alt="Logo"
                  className="relative mx-auto h-24 w-auto animate-float"
                />
              </Link>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                Welcome Back
              </h2>
              <p className="text-dark-text-secondary text-lg">
                Sign in to continue your journey
              </p>
            </div>
          </div>

          {/* Form Section */}
          {!otpMode ? (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Email Input */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -m-1" />
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    required
                    className="block w-full rounded-xl border-0 py-3 pl-12 pr-4 bg-white/5 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500/50 backdrop-blur-sm transition-all duration-300 [&:-webkit-autofill]:bg-white/5 [&:-webkit-autofill]:[transition-delay:9999s] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(255,255,255)]"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -m-1" />
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    required
                    className="block w-full rounded-xl border-0 py-3 pl-12 pr-12 bg-white/5 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500/50 backdrop-blur-sm transition-all duration-300 [&:-webkit-autofill]:bg-white/5 [&:-webkit-autofill]:[transition-delay:9999s] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(255,255,255)]"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? (
                      <FaEyeSlash className="h-5 w-5" />
                    ) : (
                      <FaEye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <Link
                  to="/register"
                  className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Don't have an account?
                </Link>
                <Link
                  to="/forgot-password"
                  className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border-0 font-medium rounded-xl text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {loading ? (
                <motion.div
                  className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              ) : (
                'Sign In'
              )}
            </button>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-gray-500 uppercase">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <GoogleLoginButton />
          </form>
          ) : (
            <form className="space-y-4" onSubmit={handleVerifyOtp}>
              <div className="space-y-4">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -m-1" />
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="otp"
                      required
                      className="block w-full rounded-xl border-0 py-3 pl-12 pr-4 bg-white/5 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500/50 backdrop-blur-sm transition-all duration-300"
                      placeholder="Enter OTP (valid 10 minutes)"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setOtpMode(false);
                      setOtp('');
                    }}
                    className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={otpLoading}
                className="group relative w-full flex justify-center py-3 px-4 border-0 font-medium rounded-xl text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {otpLoading ? (
                  <motion.div
                    className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                ) : (
                  'Verify OTP'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
